/*
 * Authors: mariushirjanu
 * Created: 06/01/2023
 */
import { trackByFunction } from '../../../../shared/formly/ga-formly.util';
import { UntypedFormGroup } from '@angular/forms';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ICatalogLayout, OrderCatalogService } from '../order-catalog.service';
import { MaxWidth, ResponsiveService } from '../../../../shared/responsive/responsive.service';
import { BreakpointsService } from '../../../../shared/services/breakpoints.service';
import { FormattingService } from '../../../../shared/services/formatting.service';
import { CatalogOverviewService } from './catalog-overview.service';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../../shared/formly/ga-formly.service';
import { ButtonAddonComponent } from '../../../../shared/formly/addons/button-addon.component';
import { ICatalogSearchParam } from './order-catalog-overview.component';
import { INamedStringObject } from '../../../../../type_definitions/datatypes';
import { CatalogLayoutSelectorFieldComponent } from './catalog-layout-selector-field.component';
import { ResizeService } from '../../../../shared/services/resize.service';

import { MAIN_CONTAINER_ELEMENT } from '../../../main.component';

@Component({
  selector: 'catalog-controls',
  template: `
    <div class="catalog-controls">
      <formly [form]="searchForm" [fields]="searchFormFields" [model]="searchParams" [noValidate]="true" class="list-controls"></formly>
    </div>
  `
})
export class CatalogControlsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public searchParams:ICatalogSearchParam;
  @Input() public selectedLayout:ICatalogLayout;
  @Input() public searching:boolean;
  @Output() public onSearch:EventEmitter<void> = new EventEmitter<void>();
  @Output() public onModifySearch:EventEmitter<void> = new EventEmitter<void>();
  @Output() public onSelectLayout:EventEmitter<ICatalogLayout> = new EventEmitter<ICatalogLayout>();

  public searchForm:UntypedFormGroup = new UntypedFormGroup({});
  public searchFormFields:IExtraFieldConfigurationObject[];
  public sortOptions:INamedStringObject[];
  public possibleLayouts:ICatalogLayout[];
  public trackByFunction:Function = trackByFunction;
  public hideLayoutSelect:boolean;

  constructor(
    private formlyService:GaFormlyService,
    private orderCatalogService:OrderCatalogService,
    private catalogOverviewService:CatalogOverviewService,
    private responsiveService:ResponsiveService,
    private breakpointsService:BreakpointsService,
    private formattingService:FormattingService,
    private resizeService:ResizeService
  ) {
    this.sortOptions = this.catalogOverviewService.sortOptions;
    this.possibleLayouts = this.catalogOverviewService.possibleLayouts;
  }

  public ngOnInit():void {
    this.activate();
    this.listen();
  }

  public ngOnChanges(changes:SimpleChanges):void {
    if (changes.searching) {
      this.searching = changes.searching.currentValue;
    }
  }

  private activate():void {
    this.searchFormFields = this.createSearchFormFields();
    this.sizeUpdate(true);
  }

  private listen():void {
    this.resizeService.addResizeEventListener(MAIN_CONTAINER_ELEMENT, () => {
      this.sizeUpdate(true);
    });
  }

  private createSearchFormFields():IExtraFieldConfigurationObject[] {
    const autoLabelClass:string = 'col col-auto';

    return [
      this.formlyService.aGroup()
        .withLabelClass('d-none')
        .withValueClass('col-md-12')
        .withFields([
          this.formlyService.anInput('terms')
            .withPlaceholder('CATALOG.FILTERS.SEARCH_PLACEHOLDER')
            .withoutWrapper()
            .withWrapperClass('col-lg-10 col-md-9 col-sm-9')
            .withAddOnButton({
              component: ButtonAddonComponent,
              inputs: {
                label: 'GA.BUTTONS.SEARCH',
                busy: () => this.searching
              },
              outputs: {
                onClick: () => this.onSearch.emit()
              }
            })
            .build(),

          this.formlyService.aCheckbox('onlyFavourites', 'CATALOG.FILTERS.FAVOURITES')
            .withoutWrapper()
            .withWrapperClass('col-lg-2 col-md-3 col-sm-3 pt-1 mb-2')
            .onChange((newVal:boolean, oldVal:boolean) => {
              if(newVal !== oldVal){
                this.onSearch.emit();
              }
            })
            .build()
        ])
        .build(),

      this.formlyService.aGroup()
        .withWrapperClass('col-12 catalog-control-options')
        .withLabelClass('d-none')
        .withValueClass('col-lg-8 col-md-9')
        .withFields([
          this.formlyService.aSelect('num', 'CATALOG.FILTERS.PPP')
            .withWrapperClass('col-lg-4 col-md-4 col-sm-4 col-6')
            .withLabelClass(autoLabelClass)
            .withValueClass('col-lg-4 col-md-4 col-sm-4')
            .withOptions(this.selectedLayout.productsPerPage.options)
            .onChange(() => {
              this.onModifySearch.emit();
            })
            .build(),

          this.formlyService.aSelect('sortCurrent', 'CATALOG.FILTERS.SORT')
            .withWrapperClass('col-lg-4 col-md-4 col-sm-4 col-6')
            .withLabelClass(autoLabelClass)
            .withValueClass('col-lg-8 col-md-8 col-sm-8')
            .withFormat(this.formattingService.formatName)
            .withOptions(this.sortOptions)
            .withValueProp('id')
            .onChange(() => {
              this.onModifySearch.emit();
            })
            .build(),

          this.formlyService.aCustom('CATALOG.FILTERS.LIST_LAYOUT')
            .withWrapperClass('col-lg-4 col-md-4 col-sm-4')
            .withLabelClass(autoLabelClass)
            .withValueClass('col-lg-6 col-md-8 col-sm-8')
            .withComponent(CatalogLayoutSelectorFieldComponent)
            .withExtraOptions({
              templateOptions: {
                inputs: {
                  selectedLayout: this.selectedLayout,
                  possibleLayouts: this.possibleLayouts
                },
                outputs: {
                  onSelectLayout: (layout:ICatalogLayout) => this.onSelectLayout.emit(layout)
                }
              }
            })
            .withHideExpression(() => this.hideLayoutSelect)
            .build()
        ])
        .build()

    ];
  }

  private sizeUpdate(doUpdateWidth:boolean):void {
    const currentBP:string = this.breakpointsService.current();
    const xSmallScreen:boolean = currentBP === 'xs';

    if(this.orderCatalogService.autoSwitchLayout) {
      // check breakpoints

      if(xSmallScreen) {
        this.hideLayoutSelect = true;
      } else {
        this.hideLayoutSelect = false;
      }
    } else {
      this.hideLayoutSelect = false;
    }

    if(doUpdateWidth) {
      this.updateWidthFromLayout();
    }
  }

  private updateWidthFromLayout() {
    this.responsiveService.width = this.selectedLayout.width || MaxWidth.MD;
  }

  public ngOnDestroy():void {
    this.resizeService.removeResizeEventListener(MAIN_CONTAINER_ELEMENT);
  }
}